import React, { useState, useEffect, useRef } from 'react';
import { schoolsAPI } from '../lib/api';
import { School, GraduationCap, Search, X, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SchoolGradeSelector Component
 * Searchable dropdown for schools + grade selector
 */
export const SchoolGradeSelector = ({ 
  familyId, 
  initialSchool = null, 
  initialGrade = null,
  initialCustomSchool = '',
  onUpdate 
}) => {
  // Schools state
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selection state
  const [selectedSchool, setSelectedSchool] = useState(initialSchool);
  const [selectedGrade, setSelectedGrade] = useState(initialGrade);
  const [customSchoolName, setCustomSchoolName] = useState(initialCustomSchool);
  const [useCustomSchool, setUseCustomSchool] = useState(!!initialCustomSchool && !initialSchool);
  
  // Search state
  const [schoolSearch, setSchoolSearch] = useState('');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  
  const schoolDropdownRef = useRef(null);
  const gradeDropdownRef = useRef(null);
  
  // Load schools and grades
  useEffect(() => {
    const loadData = async () => {
      try {
        const [schoolsRes, gradesRes] = await Promise.all([
          schoolsAPI.getSchools(),
          schoolsAPI.getGrades()
        ]);
        setSchools(schoolsRes.data || []);
        setGrades(gradesRes.data || []);
      } catch (err) {
        console.error('Error loading schools/grades:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);
  
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (schoolDropdownRef.current && !schoolDropdownRef.current.contains(event.target)) {
        setShowSchoolDropdown(false);
      }
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(event.target)) {
        setShowGradeDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Filter schools based on search
  const filteredSchools = schools.filter(school => 
    school.full_name?.toLowerCase().includes(schoolSearch.toLowerCase()) ||
    school.name?.toLowerCase().includes(schoolSearch.toLowerCase())
  );
  
  // Handle school selection
  const handleSelectSchool = (school) => {
    setSelectedSchool(school);
    setCustomSchoolName('');
    setUseCustomSchool(false);
    setShowSchoolDropdown(false);
    setSchoolSearch('');
  };
  
  // Handle custom school
  const handleCustomSchool = () => {
    setSelectedSchool(null);
    setUseCustomSchool(true);
    setShowSchoolDropdown(false);
  };
  
  // Handle grade selection
  const handleSelectGrade = (grade) => {
    setSelectedGrade(grade);
    setShowGradeDropdown(false);
  };
  
  // Save changes
  const handleSave = async () => {
    if (!familyId) return;
    
    try {
      const updateData = {
        school_directory_id: useCustomSchool ? null : selectedSchool?.id,
        custom_school_name: useCustomSchool ? customSchoolName : null,
        grade_key: selectedGrade?.grade_key
      };
      
      await schoolsAPI.updateFamilySchool(familyId, updateData);
      toast.success('School and grade updated');
      
      if (onUpdate) {
        onUpdate({
          school: selectedSchool,
          customSchoolName: useCustomSchool ? customSchoolName : null,
          grade: selectedGrade
        });
      }
    } catch (err) {
      toast.error('Failed to update school/grade');
      console.error('Error updating school/grade:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Display value for school
  const schoolDisplayValue = useCustomSchool 
    ? (customSchoolName || 'Enter school name...') 
    : (selectedSchool?.full_name || 'Select school...');
  
  // Display value for grade
  const gradeDisplayValue = selectedGrade?.label || 'Select grade...';
  
  return (
    <div className="space-y-6">
      {/* School Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
          <School className="w-4 h-4" />
          School
        </label>
        
        <div className="relative" ref={schoolDropdownRef}>
          <button
            type="button"
            onClick={() => setShowSchoolDropdown(!showSchoolDropdown)}
            className="w-full flex items-center justify-between px-4 py-3 border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors text-left"
            data-testid="school-selector"
          >
            <span className={selectedSchool || useCustomSchool ? 'text-slate-900' : 'text-slate-400'}>
              {schoolDisplayValue}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
          
          {showSchoolDropdown && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search schools..."
                    value={schoolSearch}
                    onChange={(e) => setSchoolSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                    data-testid="school-search-input"
                    autoFocus
                  />
                  {schoolSearch && (
                    <button
                      onClick={() => setSchoolSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Schools List */}
              <div className="max-h-56 overflow-y-auto">
                {filteredSchools.map((school) => (
                  <button
                    key={school.id}
                    onClick={() => handleSelectSchool(school)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                    data-testid={`school-option-${school.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{school.name}</p>
                      {school.branch && (
                        <p className="text-sm text-slate-500">{school.branch}, {school.city}</p>
                      )}
                    </div>
                    {selectedSchool?.id === school.id && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                ))}
                
                {filteredSchools.length === 0 && (
                  <div className="px-4 py-3 text-center text-slate-500 text-sm">
                    No schools found
                  </div>
                )}
              </div>
              
              {/* Custom School Option */}
              <div className="border-t border-slate-100">
                <button
                  onClick={handleCustomSchool}
                  className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 transition-colors text-left text-primary"
                  data-testid="custom-school-option"
                >
                  <span className="text-lg">+</span>
                  <span>Add custom school</span>
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Custom School Input */}
        {useCustomSchool && (
          <input
            type="text"
            placeholder="Enter school name"
            value={customSchoolName}
            onChange={(e) => setCustomSchoolName(e.target.value)}
            className="mt-2 w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            data-testid="custom-school-input"
          />
        )}
      </div>
      
      {/* Grade Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
          <GraduationCap className="w-4 h-4" />
          Grade / Class
        </label>
        
        <div className="relative" ref={gradeDropdownRef}>
          <button
            type="button"
            onClick={() => setShowGradeDropdown(!showGradeDropdown)}
            className="w-full flex items-center justify-between px-4 py-3 border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors text-left"
            data-testid="grade-selector"
          >
            <span className={selectedGrade ? 'text-slate-900' : 'text-slate-400'}>
              {gradeDisplayValue}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
          
          {showGradeDropdown && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {grades.map((grade) => (
                <button
                  key={grade.grade_key}
                  onClick={() => handleSelectGrade(grade)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`grade-option-${grade.grade_key}`}
                >
                  <span className="text-slate-900">{grade.label}</span>
                  {selectedGrade?.grade_key === grade.grade_key && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={!familyId || (!selectedSchool && !customSchoolName && !selectedGrade)}
        className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="save-school-grade-button"
      >
        Save School & Grade
      </button>
    </div>
  );
};

export default SchoolGradeSelector;
